import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import {
  Card,
  Button,
  Row,
  Col,
  Container,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { RiEyeLine, RiDownload2Line, RiShareLine } from "react-icons/ri";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Import all template components
import PremiumModernTemplate from "../Package/PackagePdfTemplates/PremiumModernTemplate";

import PackageShareModal from "./PackageShareModal";
import SimpleEleganceTemplate from "./PackagePdfTemplates/SimpleEleganceTemplate";
import ModernCorporateTemplate from "./PackagePdfTemplates/ModernCorporateTemplate";
import LuxuryMagazineTemplate from "./PackagePdfTemplates/LuxuryMagazineTemplate";
import VibrantAdventureTemplate from "./PackagePdfTemplates/VibrantAdventureTemplate";
import ClassicEleganceTemplate from "./PackagePdfTemplates/ClassicEleganceTemplate";
import GeoExplorerTemplate from "./PackagePdfTemplates/GeoExplorerTemplate";
import ArtisanJournalTemplate from "./PackagePdfTemplates/ArtisanJournalTemplate";
import SimpleTemplate from "./PackagePdfTemplates/SimpleTemplate";

// --- STYLES (UPDATED for Single Page Preview) ---
const styles = {
  pageContainer: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  templateCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    borderRadius: '0px',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.07)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  templateCardHover: {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
  // --- MODIFIED SECTION ---
  // The outer container is now a flex container to center the page
  cardPreviewContainer: {
    height: '450px', // Adjusted height for a single page view
    overflow: 'hidden', // CRITICAL: No more scrolling
    backgroundColor: '#e9ecef',
    padding: '20px', // Uniform padding
    borderBottom: '1px solid #dee2e6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  // The individual A4 page visual. Sized to fit within the container.
  a4Page: {
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    boxShadow: '0 1px 5px rgba(0,0,0,0.15)',
    overflow: 'hidden', // Crucial for clipping the content
    position: 'relative',
  },
  // Wrapper for the actual template content inside the page
  paginatedContentWrapper: {
    width: '800px', // Render at a consistent width
    position: 'absolute',
    top: 0,
    left: 0,
    transformOrigin: 'top left',
  },
  // --- END MODIFIED SECTION ---
  cardBody: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0rem',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#343a40',
    marginBottom: '1.25rem',
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
  },
};

const useElementWidth = (ref) => {
    const [width, setWidth] = useState(0);

    useLayoutEffect(() => {
        if (!ref.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            if (entries[0]) {
                setWidth(entries[0].contentRect.width);
            }
        });

        resizeObserver.observe(ref.current);

        return () => resizeObserver.disconnect();
    }, [ref]);

    return width;
};


const SinglePagePreview = ({ children }) => {
    const containerRef = useRef(null);
    const containerWidth = useElementWidth(containerRef);

    // Dynamically calculate the scale factor based on container width
    // The '800' is the original width of your template content
    const scaleFactor = containerWidth > 0 ? containerWidth / 800 : 0;

    return (
        <div ref={containerRef} style={styles.a4Page}>
            <div style={{
                ...styles.paginatedContentWrapper,
                transform: `scale(${scaleFactor})`,
            }}>
                {children}
            </div>
        </div>
    );
};
 
const PackageTemplates = () => {
  // ... All your existing state and logic remains exactly the same ...
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { user, loading, refreshUserProfile } = useAuth();
  const location = useLocation();
  const isStandalonePage = location.pathname.startsWith("/packagedetails");
  const [packageData, setPackageData] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedTemplateForAction, setSelectedTemplateForAction] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(null);
  const [templateForPdf, setTemplateForPdf] = useState(null);
  const pdfPreviewRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const allTemplates = [
    { id: "simple-elegance", name: "Simple Elegance", component: SimpleEleganceTemplate, category: 'Simple' },
    { id: "classic-elegance", name: "Classic Elegance", component: ClassicEleganceTemplate, category: 'Simple' },
    { id: "modern-corporate", name: "Modern Corporate", component: ModernCorporateTemplate, category: 'Modern' },
    { id: "vibrant-adventure", name: "Vibrant Adventure", component: VibrantAdventureTemplate, category: 'Modern' },
    { id: "geo-explorer", name: "Geo Explorer", component: GeoExplorerTemplate, category: 'Modern' },
    { id: "premium-modern", name: "Premium Modern", component: PremiumModernTemplate, category: 'Premium' },
    { id: "luxury-magazine", name: "Luxury Magazine", component: LuxuryMagazineTemplate, category: 'Premium' },
    { id: "artisan-journal", name: "Artisan Journal", component: ArtisanJournalTemplate, category: 'Premium' },
    { id: "simple-template", name: "Simple Template", component: SimpleTemplate, category: 'Simple' },
  ];

  const simpleTemplates = allTemplates.filter(t => t.category === 'Simple');
  const modernTemplates = allTemplates.filter(t => t.category === 'Modern');
  const premiumTemplates = allTemplates.filter(t => t.category === 'Premium');

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    const getData = async () => {
      if (!user || !packageId) return;
      try {
        const res = await apiService.get(`${config.GETPACKAGEDETAILS}/${packageId}`);
        setPackageData(res);
      } catch (error) {
        console.error("Error fetching package details:", error);
        toast.error("Failed to load package details.");
      }
    };
    getData();
  }, [user, packageId]);

  const renderTemplateById = (templateId) => {
    if (!packageData) return <div className="text-center p-5">Loading Preview...</div>;
    const template = allTemplates.find((t) => t.id === templateId);
    if (!template) return <div>Template not found.</div>;

    const TemplateComponent = template.component;
    return <TemplateComponent packageData={packageData} user={user} />;
  };

  const handleViewClick = (templateId) => {
    const viewUrl = `${window.location.origin}/template-preview/${packageId}/${templateId}`;
    window.open(viewUrl, '_blank');
  };

  const handleDownloadClick = (templateId) => {
    setDownloadingTemplate(templateId);
    setTemplateForPdf(templateId);
  };
   const handleShareClick = async (templateId) => {
    if (!user || !user.whatsAppNumber) {
        toast.warn("Please add your WhatsApp number in your profile to use this feature.");
        return; 
    }

    const updatedProfile = await refreshUserProfile();

    if (updatedProfile && updatedProfile.connects > 0) {
        setSelectedTemplateForAction(templateId);
        setShowShareModal(true);
    } else {
        toast.error("You have no share connects left. Please recharge to continue.");
    }
  };
  useEffect(() => {
    if (!templateForPdf || !pdfPreviewRef.current) return;
    const generateAndDownload = async () => {
      try {
        const input = pdfPreviewRef.current;
        const canvas = await html2canvas(input, { scale: 2, useCORS: true, windowWidth: input.scrollWidth, windowHeight: input.scrollHeight });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4", true);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const ratio = canvas.height / canvas.width;
        let imgHeight = pdfWidth * ratio;
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pdfHeight;
        while (heightLeft > 0) {
          position -= pdfHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight, undefined, 'FAST');
          heightLeft -= pdfHeight;
        }
        const sanitizedPackageName = packageData?.package?.packageName?.replace(/\s/g, "_") || "package";
        pdf.save(`${sanitizedPackageName}_${templateForPdf}.pdf`);
        toast.success("PDF Downloaded!");
      } catch (error) {
        console.error("Error generating PDF:", error);
        toast.error("An error occurred while generating the PDF.");
      } finally {
        setTemplateForPdf(null);
        setDownloadingTemplate(null);
      }
    };
    setTimeout(generateAndDownload, 500);
  }, [templateForPdf, packageData]);

  if (loading || !packageData) {
    return <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}><Spinner animation="border" /> <span className="ms-2">Loading Package...</span></div>;
  }

  const renderTooltip = (props, text) => (
    <Tooltip id="button-tooltip" {...props}>
      {text}
    </Tooltip>
  );

   const renderTemplateSection = (title, templates) => (
    <>
      <h2 className="mb-4 mt-5 fw-bold">{title}</h2>
      {/* --- STEP 3: UPDATE THE GRID TO BE RESPONSIVE --- */}
      <Row className="g-4">
        {templates.map((template) => (
          // Use Bootstrap's responsive columns for the grid
          <Col xs={12} md={6} lg={4} key={template.id}>
            <Card
              style={{
                ...styles.templateCard,
                ...(hoveredCard === template.id ? styles.templateCardHover : {}),
              }}
              onMouseEnter={() => setHoveredCard(template.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={styles.cardPreviewContainer}>
                <SinglePagePreview>
                  {renderTemplateById(template.id)}
                </SinglePagePreview>
              </div>
              <Card.Body style={styles.cardBody}>
                <Card.Title style={styles.cardTitle}>{template.name}</Card.Title>
                <div style={styles.actionsContainer}>
                   <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'View Template')}>
                    <Button variant="primary" onClick={() => handleViewClick(template.id)}><RiEyeLine /></Button>
                  </OverlayTrigger>

                  <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Download PDF')}>
                    <Button variant="secondary" onClick={() => handleDownloadClick(template.id)} disabled={!!downloadingTemplate}>
                      {downloadingTemplate === template.id ? ( <Spinner as="span" animation="border" size="sm" /> ) : ( <RiDownload2Line /> )}
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Share Template')}>
                    <Button variant="success" onClick={() => handleShareClick(template.id)}><RiShareLine /></Button>
                  </OverlayTrigger>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
  return (
    <div style={styles.pageContainer}>
      <Container fluid className={isStandalonePage ? "standalone-container py-4 px-md-4" : "mt-3 py-4 px-md-4"}>
        <h3 className="mb-4 fw-bold">Available Quotation Templates ({allTemplates.length})</h3>

        {renderTemplateSection("Simple & Clean", simpleTemplates)}
        {renderTemplateSection("Modern & Vibrant", modernTemplates)}
        {renderTemplateSection("Premium & Luxurious", premiumTemplates)}
      </Container>

      {packageData && selectedTemplateForAction && (
        <PackageShareModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          packageData={packageData}
          initialTemplateId={selectedTemplateForAction}
        />
      )}

      {templateForPdf && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1100px', backgroundColor: 'white' }}>
          <div ref={pdfPreviewRef}>
            {renderTemplateById(templateForPdf)}
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageTemplates;